/**
 * Support Ticket Hooks
 *
 * React hooks for managing support ticket state and operations.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Location: apps/web/src/hooks/support/useSupportTicket.js
 */

import { useState, useEffect, useCallback } from 'react';
import { supportQueueApi } from '../../services/supportApi';

/**
 * Fetch and manage a single support ticket
 */
export function useSupportTicket(ticketId) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTicket = useCallback(async () => {
    if (!ticketId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await supportQueueApi.getById(ticketId);
      setTicket(data);
    } catch (err) {
      setError(err.message || 'Failed to load ticket');
      console.error('Error fetching ticket:', err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  return {
    ticket,
    loading,
    error,
    refresh: fetchTicket,
  };
}

/**
 * Update a support ticket
 */
export function useUpdateTicket(ticketId) {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateTicket = async (updates) => {
    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }

    try {
      setUpdating(true);
      setError(null);
      const result = await supportQueueApi.update(ticketId, updates);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to update ticket');
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return {
    updateTicket,
    updating,
    error,
  };
}

/**
 * Add a note to a support ticket
 */
export function useAddNote(ticketId) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const addNote = async (noteData) => {
    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }

    try {
      setAdding(true);
      setError(null);
      const result = await supportQueueApi.addNote(ticketId, noteData);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to add note');
      throw err;
    } finally {
      setAdding(false);
    }
  };

  return {
    addNote,
    adding,
    error,
  };
}

/**
 * Fetch ticket timeline (activity history)
 */
export function useTicketTimeline(ticketId) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTimeline = useCallback(async () => {
    if (!ticketId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await supportQueueApi.getTimeline(ticketId);
      setTimeline(data);
    } catch (err) {
      setError(err.message || 'Failed to load timeline');
      console.error('Error fetching timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  return {
    timeline,
    loading,
    error,
    refresh: fetchTimeline,
  };
}

/**
 * Fetch canned responses for a specific category
 */
export function useCannedResponses(category) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchResponses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Import cannedResponseApi dynamically to avoid circular dependency
      const { cannedResponseApi } = await import('../../services/supportApi');
      const data = await cannedResponseApi.getAll({ category, is_active: true });
      setResponses(data);
    } catch (err) {
      setError(err.message || 'Failed to load canned responses');
      console.error('Error fetching canned responses:', err);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  return {
    responses,
    loading,
    error,
    refresh: fetchResponses,
  };
}

/**
 * Search canned responses
 */
export function useSearchCannedResponses() {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async (query, limit = 20) => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      return;
    }

    try {
      setSearching(true);
      setError(null);

      const { cannedResponseApi } = await import('../../services/supportApi');
      const data = await cannedResponseApi.search(query, limit);
      setResults(data);
    } catch (err) {
      setError(err.message || 'Failed to search canned responses');
      console.error('Error searching canned responses:', err);
    } finally {
      setSearching(false);
    }
  };

  return {
    results,
    searching,
    error,
    search,
  };
}
